import axios from 'axios';
import { Idea, IdeaFormData } from '../types/idea';
import { Filters } from '../types/filters';
import { store } from '../store';
import { startLoading, stopLoading } from '../store/slices/loadingSlice';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add a response interceptor
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            // Clear token and redirect to login
            localStorage.removeItem('token');
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

export const authService = {
    login: async (email: string, password: string) => {
        const response = await api.post('/api/v1/auth', { email, password });
        return response.data;
    },
    logout: async () => {
        const response = await api.post('/api/v1/user/logout');
        return response.data;
    },
};

interface IdeaFilters {
    holidays?: number[];
    genders?: number[];
    compilations?: number[];
    price_from?: number;
    price_to?: number;
}

export const ideaService = {
    getAll: async (filters: IdeaFilters = {}) => {
        const params = new URLSearchParams();
        
        if (filters.holidays?.length) {
            filters.holidays.forEach(id => params.append('holidays[]', id.toString()));
        }
        if (filters.genders?.length) {
            filters.genders.forEach(id => params.append('genders[]', id.toString()));
        }
        if (filters.compilations?.length) {
            filters.compilations.forEach(id => params.append('compilations[]', id.toString()));
        }
        if (filters.price_from) {
            params.append('price_from', filters.price_from.toString());
        }
        if (filters.price_to) {
            params.append('price_to', filters.price_to.toString());
        }

        const response = await api.get('/api/v1/ideas', { params });
        return response.data;
    },
    create: async (data: IdeaFormData) => {
        const response = await api.post('/api/v1/ideas', data);
        return response.data;
    },
    update: async (id: number, data: IdeaFormData) => {
        try {
            console.log('Sending update request for idea:', id, 'with data:', data);
            const response = await api.put(`/api/v1/ideas/${id}`, data);
            console.log('Update response:', response);
            if (response.data) {
                return response.data;
            } else {
                console.error('Invalid response format:', response);
                throw new Error('Invalid response format');
            }
        } catch (error) {
            console.error('Error in update request:', error);
            throw error;
        }
    },
    delete: async (id: number) => {
        const response = await api.delete(`/api/v1/ideas/${id}`);
        return response.data;
    },
};

export const filtersService = {
    getFilters: async (): Promise<Filters> => {
        const response = await api.get('/api/v1/wish/recommendations/filters');
        return response.data;
    },
}; 